import { ContractInfo, PoolInfo } from 'api/types/httpsTypes/contracts';
import { useQueryParams } from 'modules/routing/useQueryParams';
import React from 'react';
import { ResizableBox, ResizeCallbackData } from 'react-resizable';
import { DexChart } from '../../../components/Chart/DexChart';
import GeckoTerminalPlaceHolder from '../../../../../assets/img/token/mockup-geckoterminal.png';
import { PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { SwapsTerminal } from '../../stats/swaps';
import * as yup from 'yup';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { CallsInfo } from '../../stats/callInfo';
import { twMerge } from 'tailwind-merge';
import createPersistedState from 'use-persisted-state';
import { HoldersComponent } from '../../stats/holders';
import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { duration } from 'moment';
import { formatNumberWithSuffix, safeBig } from 'utils/FormatNumber';
import Button from 'modules/shared-components/button/button';
import { OpenTradesComponent } from '../../../dashboard/components/trades/openTrades';
import { HistoryTradesComponent } from '../../../dashboard/components/trades/historyTrades';
import { OpenTradesFilterProvider } from '../../../dashboard/components/trades/usePageParams';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { OpenTradesFiltersBar } from '../../../dashboard/components/trades/OpenTradesFilters';
import { SwapsFilterBar } from '../../stats/SwapsFilters';

export enum AnalyticsKeys {
  Security = 'security',
  Info = 'info',
  Trade = 'trade',
  Trades = 'trades',
  History = 'history',
  SmartSwaps = 'swaps',
  MainStats = 'main-stats',
  SmartStats = 'smart-stats',
  TelegramCalls = 'telegram-calls',
  Holders = 'holders',
}

export function useSchemaPage() {
  const isDesktop = useMediaQuery(screenGte.large);
  return {
    activeAnalytics: yup
      .string()
      .default(isDesktop ? AnalyticsKeys.Trades : undefined)
      .oneOf(Object.values(AnalyticsKeys)),
  };
}

const chartHeightLocalStorage = `chartHeight`;
export const useChartHeight = createPersistedState<number>(
  chartHeightLocalStorage
);

export interface SwapsChart {
  price: number;
  timestamp: string;
}

export function TokenInfoTradeBody({
  contract,
  selectedPool,
}: {
  contract: ContractInfo;
  selectedPool: PoolInfo | undefined;
}) {
  const [queryParams, setQueryParams] = useQueryParams(useSchemaPage());
  const [chartHeight, setChartHeight] = useChartHeight(350);

  const handleResize = (_e: React.SyntheticEvent, data: ResizeCallbackData) => {
    const newHeight = data.size.height;
    setChartHeight(newHeight);
    document
      .querySelector('.dex-chart-wrapper')
      ?.classList.remove('pointer-events-none');
  };

  const params = {
    tokenAddress: contract.generalInfo.address,
  };

  const {
    data: mainData,
    error,
    isLoading,
  } = useQuery({
    queryKey: contractServiceKeys.getHolders(params),
    queryFn: () => ContractService.getHolders(params),
    keepPreviousData: true,
    enabled: contract.generalInfo.chain.key === Chains.Solana,
    refetchInterval: duration(12, 'seconds').asMilliseconds(),
  });

  const AnalyticsButton = ({
    label,
    keyValue,
    isVisible = true,
    extraContent,
  }: {
    label: string;
    keyValue: AnalyticsKeys;
    isVisible?: boolean;
    extraContent?: any;
  }) => {
    if (!isVisible) return null;

    const isActive = queryParams.activeAnalytics === keyValue;

    return (
      <Button
        type="button"
        onClick={() => setQueryParams({ activeAnalytics: keyValue })}
        className={twMerge(
          'xxs:p-2 xxs:rounded-none xxs:border-solid border-transparent border-x-0 border-t-0',
          isActive
            ? 'xxs:text-dex-white xxs:border-solid border-white'
            : 'text-dex-white-secondary hover:text-dex-white'
        )}
      >
        <div className="flex space-x-1 items-center justify-center">
          <div className="normal-case xxs:pt-1">
            {label}
            {extraContent}
          </div>
        </div>
      </Button>
    );
  };

  return (
    <>
      <div className="space-y-1 flex flex-col grow">
        <ResizableBox
          height={chartHeight}
          width={Infinity}
          axis="y"
          handle={
            <div className="hidden lg:block resize-handle bg-dex-black-700 h-2 w-full cursor-row-resize" />
          }
          minConstraints={[200, 100]}
          maxConstraints={[Infinity, 1000]}
          resizeHandles={['s']}
          onResizeStart={() => {
            document
              .querySelector('.dex-chart-wrapper')
              ?.classList.add('pointer-events-none');
          }}
          onResizeStop={handleResize}
        >
          {selectedPool ? (
            <div className="xxs:h-full">
              {contract && (
                <DexChart
                  contract={contract}
                  poolAddress={selectedPool.poolAddress}
                />
              )}
            </div>
          ) : (
            <div className="overflow-x-hidden">
              <div
                style={{ height: chartHeight }}
                className="w-full xxs:relative xxs:bg-dex-black-900 flex items-center justify-center"
              >
                <img
                  src={GeckoTerminalPlaceHolder}
                  className="h-full"
                  style={{ filter: 'blur(5px)' }}
                  alt="Blurred Image"
                />
                <div className="absolute xxs:text-dex-white-secondary top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-10">
                  <PuzzlePieceIcon className="h-10 w-10" />
                  <p className="text-lg ">
                    {contract.status === 'RUG_PULL'
                      ? 'This token has been rug pulled'
                      : 'This token has not been launched yet'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </ResizableBox>
        <OpenTradesFilterProvider>
          <div className="hidden lg:block">
            <div className="flex justify-between items-center">
              <div
                className={twMerge(
                  'hidden flex xxs:pl-4 xxs:gap-4 border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-dex-black-700 w-full'
                )}
              >
                <AnalyticsButton
                  label="Open Trades"
                  keyValue={AnalyticsKeys.Trades}
                />
                <AnalyticsButton
                  label="History"
                  keyValue={AnalyticsKeys.History}
                />
                <AnalyticsButton
                  label="Swaps"
                  keyValue={AnalyticsKeys.SmartSwaps}
                />
                <AnalyticsButton
                  label="Holders"
                  keyValue={AnalyticsKeys.Holders}
                  isVisible={contract.generalInfo.chain.key === Chains.Solana}
                  extraContent={
                    mainData?.numberOfHolders
                      ? ` (${formatNumberWithSuffix(
                          safeBig(mainData.numberOfHolders),
                          { precision: 0 }
                        )})`
                      : ' (--)'
                  }
                />
                <AnalyticsButton
                  label="Telegram Calls"
                  keyValue={AnalyticsKeys.TelegramCalls}
                  isVisible={contract.generalInfo.chain.key !== Chains.Solana}
                />
              </div>

              {(queryParams.activeAnalytics === AnalyticsKeys.Trades ||
                queryParams.activeAnalytics === AnalyticsKeys.History) && (
                <OpenTradesFiltersBar isTerminal={true} />
              )}
              {queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps && (
                <SwapsFilterBar contract={contract} />
              )}
            </div>

            {contract.generalInfo.chain.key !== Chains.Solana &&
              queryParams.activeAnalytics === AnalyticsKeys.TelegramCalls && (
                <CallsInfo contract={contract} />
              )}

            {queryParams.activeAnalytics === AnalyticsKeys.Trades && (
              <OpenTradesComponent isTerminal={true} />
            )}

            {queryParams.activeAnalytics === AnalyticsKeys.History && (
              <HistoryTradesComponent isTerminal={true} />
            )}

            {queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps && (
              <SwapsTerminal contract={contract} />
            )}

            {queryParams.activeAnalytics === AnalyticsKeys.Holders && (
              <HoldersComponent
                mainData={mainData}
                error={error}
                isLoading={isLoading}
                contract={contract}
              />
            )}
          </div>
        </OpenTradesFilterProvider>
      </div>
    </>
  );
}
